/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

/**
 * project-store.js — THE PROJECT IS THE DOCUMENT. One file holds the whole
 * design: every desktop, and every ROM image those desktops were programmed
 * with.
 *
 *     <name>.chiphippo
 *     {
 *       version, name, description?, wheelLocked?, activeTab, nextIndex,
 *       tabs:   [ { id, name, description?, doc } ],   // doc = a desk document
 *       images: { <rom-guid>: <base64> },              // programmed ROMs only
 *       connections?: [ {id, name, baud, …} ],         // serial, NO port
 *       codegen?: { <tabId>: { <connId>: "0x…" } },    // generated headers
 *       cpuBreakpoints?: { <tabId>: { <compId>: [addr…] } }, // CPU monitor
 *       customChips?: [ {id, name, ports, units, code, …} ] // designed chips
 *     }
 *
 * Up to v3 a project was a list of PATHS — one `.desktop.chiphippo` per tab,
 * anywhere on disk — and the ROM bytes were not in it at all. Two file
 * lifetimes had to be kept referentially consistent, which is where the
 * app-kept flags, the orphan collection, and the eager write-through all came
 * from; and it could not do the one thing a real file was for, since a project
 * carried to another machine had a dead path per tab and no ROM contents.
 * v4 inlines the lot. `project-migrate.js` brings a v3 file forward.
 *
 * WHAT IS LEFT HERE is only the shape of that one file:
 *
 *   · `newProject()` — a blank project, ONE desktop, written to the working
 *     slot so the next launch finds it.
 *   · `read` / `write` — the file, whole. Reading HYDRATES the memory cache
 *     from the image tables; writing COLLECTS it back (project-images.js).
 *     Nothing outside the file is needed to open a design.
 *   · the working slot — an UNSAVED project (blank name, blank location) lives
 *     in the one fixed `saves/default.chiphippo`, which is to a project
 *     exactly what desk.json once was to a schematic: the always-there slot
 *     the app boots onto. `location` is never STORED — it IS the path, so it
 *     cannot disagree with reality, and it reads back blank for that file.
 *   · desktop SNAPSHOTS — the `.desktop.chiphippo` interchange fragment behind
 *     Export/Import. A snapshot is a copy with no retained link, so unlike the
 *     v3 desktop file it can never dangle.
 *
 * This module does not own dialogs, the MRU list, or the decision of which
 * path to write to — main.js does, and it is the only caller.
 */
"use strict";

const fs = require("fs");
const path = require("path");
const io = require("./io");
const { defaultDeskDocument, migrateDeskDocument } = require("./migrations");
const {
  isProjectShape,
  isLegacyProject,
  migrateLegacyTabs,
} = require("./project-migrate");
const { collectImages, imagesOf, hydrateImages } = require("./project-images");
const { sanitizeCustomChips } = require("./custom-chips");

/**
 * Schema version of a project file. v4 was the single-file redesign: every
 * desktop's document and every programmed ROM's bytes moved INTO the file, so
 * a project has no companion files at all. v5 splits those bytes into a
 * content-addressed `blobs` table every desktop shares, leaving `images` as
 * one small reference per chip (project-images.js). A v4 file still reads —
 * `imagesOf` tells the two shapes apart — and v3 (paths per tab) is migrated
 * on read; the v1/v2 folder projects never left the app's own directory and
 * are not migrated.
 */
const PROJECT_VERSION = 5;

/** The app's own folder for files the user hasn't chosen a home for yet. */
const SAVES_DIR = "saves";

/** The memory cache `images` hydrates into (Feature 180's `.bin` sidecars). */
const MEMORY_DIR = "memory";

/** The project IS the document, so it takes the plain extension. */
const PROJECT_EXT = ".chiphippo";

/** The interchange fragment behind Export/Import — a snapshot, never a link. */
const DESKTOP_EXT = ".desktop.chiphippo";

/** v3's project extension. Still readable; never written. */
const LEGACY_PROJECT_EXT = ".project.chiphippo";

/** The one fixed file an UNSAVED (blank-name, blank-location) project lives in. */
const DEFAULT_PROJECT_FILE = `default${PROJECT_EXT}`;
const LEGACY_DEFAULT_PROJECT_FILE = `default${LEGACY_PROJECT_EXT}`;

/** How long a name may be (it becomes a suggested file name). */
const MAX_NAME = 64;

/** What no file name should carry: the control range and the path/shell set.
    (A control character is exactly what has to be stripped out of a name that
    becomes a file name, hence the disable.) */
// eslint-disable-next-line no-control-regex
const UNSAFE_FILENAME_RE = /[\x00-\x1f<>:"\\/|?*]+/g;

/** What a serial connection id may look like (the renderer's own rule). */
const CONNECTION_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/i;

/** A generated header's design hash, as the project stores it. */
const HASH_RE = /^0x[0-9A-F]{8}$/;

/**
 * The serial connections a project carries (the Arduino serial integration):
 * each one its elements name, WITHOUT a port — a port is a fact about one
 * machine, and the settings there are where it lives. Only the plain fields
 * survive; the renderer (model/serial-connections.js) is the one that decides
 * what their values mean.
 */
function sanitizeConnections(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const c of list) {
    if (!c || typeof c !== "object") continue;
    if (typeof c.id !== "string" || !CONNECTION_ID_RE.test(c.id)) continue;
    if (c.id === "mock") continue; // the built-in Mock is everywhere already
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    const record = { id: c.id, name: text(c.name).slice(0, MAX_NAME) || c.id };
    for (const key of ["baud", "dataBits", "stopBits"]) {
      if (Number.isFinite(c[key])) record[key] = c[key];
    }
    for (const key of ["parity", "flowControl", "language"]) {
      if (typeof c[key] === "string") record[key] = c[key].slice(0, 16);
    }
    out.push(record);
  }
  return out;
}

/** The generated-header hashes, per desktop per connection, pruned to the
    desktops that exist. */
function sanitizeCodegen(raw, tabIds) {
  const out = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const tabId of tabIds) {
    const entry = raw[tabId];
    if (!entry || typeof entry !== "object") continue;
    const kept = {};
    for (const [connId, hash] of Object.entries(entry)) {
      if (CONNECTION_ID_RE.test(connId) && HASH_RE.test(String(hash))) {
        kept[connId] = hash;
      }
    }
    if (Object.keys(kept).length) out[tabId] = kept;
  }
  return out;
}

/** A placed component's id, as a breakpoint map keys it. */
const COMPONENT_ID_RE = /^c\d{1,9}$/;

/** The most breakpoints one CPU keeps. */
const MAX_CPU_BREAKPOINTS = 256;

/**
 * The CPU monitor's breakpoints — `{tabId: {compId: [addr…]}}` — held to
 * their shape: desktops that exist, component ids, whole 16-bit addresses,
 * ascending and once each. Main's own guard, as for `codegen`
 * (model/project-doc.js keeps the renderer's).
 */
function sanitizeCpuBreakpoints(raw, tabIds) {
  const out = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const tabId of tabIds) {
    const entry = raw[tabId];
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const kept = {};
    for (const [compId, list] of Object.entries(entry)) {
      if (!COMPONENT_ID_RE.test(compId) || !Array.isArray(list)) continue;
      const addrs = [
        ...new Set(
          list.filter((a) => Number.isInteger(a) && a >= 0 && a <= 0xffff),
        ),
      ].sort((a, b) => a - b);
      if (addrs.length) kept[compId] = addrs.slice(0, MAX_CPU_BREAKPOINTS);
    }
    if (Object.keys(kept).length) out[tabId] = kept;
  }
  return out;
}

function taggedError(message, code) {
  const err = new Error(message);
  err.code = code;
  return err;
}

/**
 * A display name reduced to a safe file name — the DEFAULT a Save As dialog
 * opens with, never a path. Spaces survive ("6502 SBC.chiphippo" reads better
 * than a slug); only what an OS would choke on is stripped.
 */
function suggestFileName(name, ext, fallback = "untitled") {
  const clean = String(name ?? "")
    .replace(UNSAFE_FILENAME_RE, " ")
    .replace(/\s+/g, " ")
    .replace(/^[.\s]+|[.\s]+$/g, "")
    .slice(0, MAX_NAME)
    .trim();
  // Windows opens CON, NUL, COM1… as devices, whatever the extension.
  const safe = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(clean.split(".")[0].trim()) ? `_${clean}` : clean; // prettier-ignore
  return `${safe || fallback}${ext}`;
}

/**
 * A file name reduced to a display name — Save As names an untitled project
 * from the file the user picked, so there is no separate "name this project"
 * dialog to sit in front of the save panel.
 */
function nameFromFile(filePath) {
  const base = path.basename(String(filePath ?? ""));
  for (const ext of [DESKTOP_EXT, LEGACY_PROJECT_EXT, PROJECT_EXT, ".json"]) {
    if (base.toLowerCase().endsWith(ext)) return base.slice(0, -ext.length);
  }
  return base;
}

/** Trim a user-supplied string field, or "" for anything that isn't one. */
function text(value) {
  return typeof value === "string" ? value.trim() : "";
}

class ProjectStore {
  /**
   * @param {string} dataDir - the app's userData directory.
   * @param {{readFile: Function, writeFile: Function}} deskStore - reads a
   *   desktop document from a legacy v3 file (migrations included), so this
   *   module never parses one itself.
   */
  constructor(dataDir, deskStore) {
    this._saves = path.join(dataDir, SAVES_DIR);
    this._memory = path.join(dataDir, MEMORY_DIR);
    this._desk = deskStore;
    this._slotHeld = false; // see holdDefaultProject
  }

  /** The app's saves directory (created on demand — see `ensureSaves`). */
  get savesDir() {
    return this._saves;
  }

  /** The memory cache `images` is hydrated into / collected from. */
  get memoryDir() {
    return this._memory;
  }

  /** Where an unsaved project (blank name, blank location) is kept. */
  get defaultProjectPath() {
    return path.join(this._saves, DEFAULT_PROJECT_FILE);
  }

  /** v3's working slot, upgraded in place on the first launch after v4. */
  get legacyDefaultProjectPath() {
    return path.join(this._saves, LEGACY_DEFAULT_PROJECT_FILE);
  }

  /** Create the saves directory if this is the app's first run. */
  ensureSaves() {
    io.ensureDir(this._saves);
    return this._saves;
  }

  /** Is `filePath` inside the app's own saves directory? */
  /**
   * Parse a file this store was asked to read. Only the app's own working slot
   * may be quarantined when it is corrupt; any other path is the user's file,
   * and is reported unreadable where it lies rather than renamed.
   */
  _readRaw(filePath) {
    const own =
      typeof filePath === "string" &&
      path.resolve(filePath) === path.resolve(this.defaultProjectPath);
    return io.readJSON(filePath, { quarantine: own });
  }

  isInsideSaves(filePath) {
    if (typeof filePath !== "string" || !filePath) return false;
    const resolved = path.resolve(filePath);
    const root = path.resolve(this._saves);
    return resolved === root || resolved.startsWith(root + path.sep);
  }

  /** Is the unsaved project's fixed file on disk? (the startup question) */
  hasDefaultProject() {
    return existsSafe(this.defaultProjectPath);
  }

  /**
   * A brand-new project: BLANK name, BLANK location, exactly one desktop
   * ("Desktop 1", an empty desk). Its home until it is saved is the fixed
   * default project file, which this writes — so the next launch finds it
   * there, and a force-quit before the first save still opens onto it.
   *
   * @returns {object} the meta, `location: null`.
   */
  newProject() {
    this.ensureSaves();
    // There is ONE working slot, and this takes it over. Nothing but the file
    // itself has to go now: a project has no companion files to collect.
    // (A HELD slot is not taken: the project is written nowhere until Save As.)
    this.removeDefaultProject();
    const meta = {
      version: PROJECT_VERSION,
      name: "",
      description: "",
      activeTab: "t1",
      nextIndex: 2,
      tabs: [{ id: "t1", name: "Desktop 1", doc: defaultDeskDocument() }],
    };
    if (!this._slotHeld) this.write(this.defaultProjectPath, meta);
    return { ...meta, location: null };
  }

  /**
   * Read a project file, WHOLE — every desktop's document with it, and the
   * memory cache refilled from its `images` so a ROM's backing file is there
   * before the renderer has seen the project.
   *
   * Three shapes come through here: a v4 project, a v3 project (inlined by
   * project-migrate.js), and a bare desk document or desktop snapshot (a loose
   * `.chiphippo` / `.desktop.chiphippo`), which becomes a project of one
   * desktop. Returns null only when the file holds none of them.
   *
   * `location` is BLANK for the fixed default file: that project has never
   * been given a home, which is exactly what the Properties dialog shows.
   * `warnings` (when present) is what the migration could not bring across —
   * neither field is ever stored.
   */
  read(filePath, { hydrate = true, inlined = null } = {}) {
    const raw = this._readRaw(filePath);
    if (!raw || typeof raw !== "object") return null;
    let meta = null;
    let warnings = [];
    const wasProject = isProjectShape(raw);
    if (wasProject) {
      if (isLegacyProject(raw)) {
        const upgraded = migrateLegacyTabs(raw, {
          deskStore: this._desk,
          savesDir: this._saves,
          // The working slot's paths are main's own; any other project's
          // may inline only what sits beside it (project-migrate.js).
          projectDir: this.isSlotPath(filePath)
            ? null
            : path.dirname(path.resolve(filePath)),
        });
        warnings = upgraded.warnings;
        for (const file of upgraded.inlined) inlined?.add(file);
        meta = this._normalize({ ...raw, tabs: upgraded.tabs }, true);
      } else {
        meta = this._normalize(raw, true);
      }
    } else {
      // Not a project at all: a loose design, or an exported desktop.
      const snapshot = this._asSnapshot(raw, nameFromFile(filePath));
      if (snapshot) meta = this._normalize(projectOf(snapshot), true);
    }
    if (!meta) return null;
    if (hydrate) hydrateImages(imagesOf(raw), this._memory);
    const resolved = path.resolve(filePath);
    // A project knows where it lives — EXCEPT in the working slot, which is
    // what "no location" means. A loose design is not a project file at all,
    // so it has no location either: Save As is what gives the project a home,
    // and nothing can overwrite the design that was imported.
    meta.location = !wasProject || this.isSlotPath(resolved) ? null : resolved;
    if (warnings.length) meta.warnings = warnings;
    return meta;
  }

  /**
   * Write a project file. The caller decides WHERE (its location, or the fixed
   * default file for a project that has none); only the fields this module
   * owns are written, so `location` — which is the path itself — never becomes
   * a stored field that could disagree with reality.
   *
   * Every programmed ROM's bytes are collected out of the memory cache and
   * written INTO the file, which is what makes it self-contained — once each,
   * however many chips across however many desktops hold the same image.
   */
  write(filePath, meta, { recoveryFor = null } = {}) {
    if (this._slotHeld && this._isSlot(filePath)) {
      throw taggedError("the working slot holds unread work", "SLOT_HELD");
    }
    const clean = this._normalize(meta);
    if (!clean) throw taggedError("a project needs a desktop", "INVALID_ARG");
    const { images, blobs } = collectImages(clean, this._memory);
    io.ensureDir(path.dirname(filePath));
    io.writeJSON(filePath, {
      version: PROJECT_VERSION,
      name: clean.name,
      ...(clean.description ? { description: clean.description } : {}),
      // The desk padlock, written only while it is SHUT — so a project that
      // never touched it keeps the bytes it always had, and an older build
      // reading this simply drops the key and opens with the wheel live.
      ...(clean.wheelLocked ? { wheelLocked: true } : {}),
      activeTab: clean.activeTab,
      nextIndex: clean.nextIndex,
      tabs: clean.tabs.map((tab) => ({
        id: tab.id,
        name: tab.name,
        ...(tab.description ? { description: tab.description } : {}),
        doc: tab.doc,
      })),
      // `images` is non-empty exactly when `blobs` is, so one guard covers the
      // pair and an image-less project still writes neither key.
      ...(Object.keys(images).length ? { images, blobs } : {}),
      // The serial integration's two: omitted while empty, so a project that
      // never used it keeps the bytes it always had.
      ...(clean.connections.length ? { connections: clean.connections } : {}),
      ...(Object.keys(clean.codegen).length ? { codegen: clean.codegen } : {}),
      // The CPU monitor's breakpoints, likewise.
      ...(Object.keys(clean.cpuBreakpoints).length
        ? { cpuBreakpoints: clean.cpuBreakpoints }
        : {}),
      // The chips the user designed: omitted while there are none, for the
      // same reason.
      ...(clean.customChips.length ? { customChips: clean.customChips } : {}),
      // Only ever in the working slot, and only when the project it belongs to
      // has a file of its own (see `writeRecovery`).
      ...(recoveryFor ? { recoveryFor } : {}),
    });
    return filePath;
  }

  /**
   * Stash the open project in the working slot as a RECOVERY copy, without
   * touching the file it belongs to.
   *
   * `recoveryFor` is the path of that file, and it is what makes the slot mean
   * something different from its usual job. Normally the slot IS an unsaved
   * project's home; stamped, it is a copy of a project that has a home already,
   * holding work that is not in it yet. So the stamp is also the CRASH FLAG:
   * the slot is dropped by a save and by a clean quit, which leaves "a stamped
   * slot exists at startup" meaning exactly "we went away without finishing".
   * No timestamps to compare — mtimes are the one thing cloud sync and a
   * corrected clock will both lie about.
   *
   * ROM bytes ride along like any other write, so a recovered project is whole.
   */
  writeRecovery(meta, recoveryFor) {
    this.ensureSaves();
    return this.write(this.defaultProjectPath, meta, { recoveryFor });
  }

  /**
   * What the working slot is, WITHOUT reading it properly: null when there is
   * no slot, `{recoveryFor, name}` when it holds a recovery copy, and
   * `{recoveryFor: null, name}` when it is an ordinary unsaved project.
   *
   * Deliberately does not go through `read`, which HYDRATES the file's ROM
   * images into the memory cache. Startup has to know whether a recovery is
   * waiting before it can ask about it, and a recovery the user then DISCARDS
   * must not have overwritten the real project's ROM files on the way past.
   */
  peekRecovery() {
    // NOT quarantined on the way past: a damaged slot may be an untitled
    // project's only copy, and set aside under io's `.corrupt-…` name, with
    // nothing said, it was out of File ▸ Open's sight. It THROWS instead, so
    // the boot keeps it as it keeps one it could not read — moved aside to a
    // `.chiphippo` name, and the user told where (boot-slot.js).
    const raw = io.readJSON(this.defaultProjectPath, { quarantine: false });
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
      if (raw == null && !existsSafe(this.defaultProjectPath)) return null;
      throw Object.assign(new Error("the working slot is damaged"), {
        code: "EDAMAGED",
      });
    }
    const recoveryFor =
      typeof raw.recoveryFor === "string" && raw.recoveryFor
        ? raw.recoveryFor
        : null;
    return { recoveryFor, name: typeof raw.name === "string" ? raw.name : "" };
  }

  /**
   * Make a stamped slot an ORDINARY one — the untitled project's home — by
   * dropping its `recoveryFor`, its bytes otherwise left exactly as they are
   * (no hydrate, no collect). For a recovery restored HOMELESS: the project it
   * stood for has gone, so it is untitled now, and an untitled project's slot
   * is its file, which a clean quit keeps. Left stamped, a quit that discarded
   * it before the first autosave kept it as a crash flag, and every launch
   * after restored it again. False when there was no stamp (or the slot is
   * held) — nothing changed.
   */
  unstampRecovery() {
    if (this._slotHeld) return false;
    const raw = io.readJSON(this.defaultProjectPath);
    if (!raw || typeof raw !== "object" || !("recoveryFor" in raw))
      return false;
    const { recoveryFor: _stamp, ...rest } = raw;
    io.writeJSON(this.defaultProjectPath, rest);
    return true;
  }

  /**
   * Bring a stored (or renderer-supplied) project to the current shape.
   *
   * `fromDisk` runs each desktop's document through the schema migrations, so
   * an older desk inside a project file comes forward. It is deliberately NOT
   * done on the way out: a write must store what the renderer holds, never a
   * re-derived version of it.
   */
  _normalize(raw, fromDisk = false) {
    if (!raw || typeof raw !== "object" || !Array.isArray(raw.tabs)) {
      return null;
    }
    const ids = new Set();
    const tabs = [];
    for (const tab of raw.tabs) {
      if (!tab || typeof tab !== "object") continue;
      const id = text(tab.id);
      if (!id || ids.has(id)) continue;
      const doc = tab.doc;
      if (!doc || typeof doc !== "object" || Array.isArray(doc)) continue;
      ids.add(id);
      tabs.push({
        id,
        name: text(tab.name) || id,
        ...(text(tab.description)
          ? { description: text(tab.description) }
          : {}),
        doc: fromDisk ? migrateDeskDocument(doc) : doc,
      });
    }
    if (tabs.length === 0) return null;
    const highest = tabs.reduce((max, tab) => {
      const n = /^t(\d+)$/.exec(tab.id);
      return n ? Math.max(max, Number(n[1])) : max;
    }, 0);
    return {
      version: PROJECT_VERSION,
      name: text(raw.name).slice(0, MAX_NAME),
      description: text(raw.description),
      wheelLocked: raw.wheelLocked === true,
      activeTab: tabs.some((t) => t.id === raw.activeTab)
        ? raw.activeTab
        : tabs[0].id,
      nextIndex:
        Number.isInteger(raw.nextIndex) && raw.nextIndex > highest
          ? raw.nextIndex
          : highest + 1,
      tabs,
      connections: sanitizeConnections(raw.connections),
      codegen: sanitizeCodegen(
        raw.codegen,
        tabs.map((t) => t.id),
      ),
      cpuBreakpoints: sanitizeCpuBreakpoints(
        raw.cpuBreakpoints,
        tabs.map((t) => t.id),
      ),
      customChips: sanitizeCustomChips(raw.customChips),
    };
  }

  // ── Desktop snapshots (Export / Import) ──────────────────────────────────
  //
  // A `.desktop.chiphippo` is a SNAPSHOT — one desktop, its ROM images with
  // it, and no link back to the project it came from. That is the whole point:
  // a copy cannot dangle, which is what the v3 desktop file could not promise.

  /**
   * Read a desktop snapshot for Import. Accepts a snapshot, a bare desk
   * document (a loose `.chiphippo` design), or a whole project — from which
   * the ACTIVE desktop is taken, since importing "a project" into a tab can
   * only mean one of its desks.
   *
   * @returns {{name: string, description: string, doc: object,
   *   images: object|null}|null}
   */
  readDesktopSnapshot(filePath) {
    const raw = this._readRaw(filePath);
    if (!raw || typeof raw !== "object") return null;
    if (isProjectShape(raw)) {
      // Migrates, but must NOT hydrate: the file's chips carry the guids they
      // were saved with, and when it is a copy or a backup of the project that
      // is OPEN, those are the open project's own sidecars — hydrating wrote the
      // file's old bytes over them. The bytes travel as `images` instead, and
      // the reseat gives them to fresh guids.
      const meta = this.read(filePath, { hydrate: false });
      const tab = meta?.tabs.find((t) => t.id === meta.activeTab);
      if (!tab) return null;
      return {
        name: tab.name,
        description: tab.description ?? "",
        doc: tab.doc,
        // The project's designed chips travel with its desktop; the renderer
        // keeps the ones the desktop actually uses.
        customChips: meta.customChips,
        images: imagesOf(raw),
      };
    }
    return this._asSnapshot(raw, nameFromFile(filePath));
  }

  /**
   * Write one desktop out as a self-contained snapshot: its document, and the
   * bytes of every programmed ROM on it.
   */
  writeDesktopSnapshot(filePath, { name, description, doc, customChips }) {
    if (!doc || typeof doc !== "object" || Array.isArray(doc)) {
      throw taggedError("a desktop needs a document", "INVALID_ARG");
    }
    const { images, blobs } = collectImages({ doc }, this._memory);
    const chips = sanitizeCustomChips(customChips);
    io.ensureDir(path.dirname(filePath));
    io.writeJSON(filePath, {
      version: PROJECT_VERSION,
      kind: "desktop",
      name: text(name) || "Desktop",
      ...(text(description) ? { description: text(description) } : {}),
      doc,
      ...(Object.keys(images).length ? { images, blobs } : {}),
      // The designed chips the desktop uses — a snapshot is self-contained,
      // so a chip on it travels with it.
      ...(chips.length ? { customChips: chips } : {}),
    });
    return filePath;
  }

  /** A parsed file as a desktop snapshot: the explicit shape, or a bare doc. */
  _asSnapshot(raw, fallbackName) {
    if (raw.kind === "desktop" && raw.doc && typeof raw.doc === "object") {
      return {
        name: text(raw.name) || fallbackName || "Desktop",
        description: text(raw.description),
        doc: migrateDeskDocument(raw.doc),
        // Flattened to the plain `guid → base64` map a reseat expects, from
        // whichever shape the snapshot was written in.
        images: imagesOf(raw),
        customChips: sanitizeCustomChips(raw.customChips),
      };
    }
    // A loose desk document. `migrateDeskDocument` answers with the default
    // empty desk for junk, which would silently import a blank tab — so only
    // a shape that actually looks like a desk gets through.
    if (!Array.isArray(raw.boards) && !Array.isArray(raw.components)) {
      return null;
    }
    return {
      name: fallbackName || "Desktop",
      description: "",
      doc: migrateDeskDocument(raw),
      images: null,
      customChips: [],
    };
  }

  // ── The working slot ─────────────────────────────────────────────────────

  /**
   * Drop the fixed default project file — the project that lived in the
   * working slot has been saved somewhere real (or replaced). There is nothing
   * else to clean up: a v4 project has no companion files.
   *
   * @returns {boolean} whether a file was deleted.
   */
  removeDefaultProject() {
    if (this._slotHeld) return false;
    return unlinkSafe(this.defaultProjectPath);
  }

  /**
   * Leave the working slot ALONE for the rest of the session: a recovery that
   * could not be read, and could not be moved aside either, may hold the only
   * copy of the work. Nothing then writes or removes it — a new project lives
   * nowhere until Save As, an autosave into it fails (and retires quietly),
   * and the next launch tries to read it again.
   */
  holdDefaultProject() {
    this._slotHeld = true;
  }

  _isSlot(filePath) {
    return this.isSlotPath(filePath);
  }

  /**
   * Does `filePath` name the working slot — v4's or v3's — however it is
   * spelt? The slot is the app's own file, never a place to save a project or
   * export a desktop to, and a plain string compare was how it became one: on
   * a case-insensitive volume a Save As to `Default.chiphippo` WROTE the slot,
   * was judged "a different file", and then deleted it as the slot it had
   * left; `default.project.chiphippo` was swallowed as v3's slot at the next
   * launch. So: the same path in any case (the slot may not exist yet, so
   * there may be no file to compare), or the same file by identity.
   */
  isSlotPath(filePath) {
    if (typeof filePath !== "string" || !filePath) return false;
    const resolved = path.resolve(filePath);
    return [this.defaultProjectPath, this.legacyDefaultProjectPath].some(
      (slot) =>
        resolved.toLowerCase() === path.resolve(slot).toLowerCase() ||
        sameFile(resolved, slot),
    );
  }

  /**
   * Why a Save As / Export target may not be written, or null when it may:
   * `"slot"` for the working slot (`isSlotPath`), `"open"` for `openProject`
   * — the open project's own file, which a desktop exported over it would
   * reduce to that one desktop while the window still showed it saved.
   */
  saveClash(filePath, openProject = null) {
    if (this.isSlotPath(filePath)) return "slot";
    if (typeof openProject !== "string" || !openProject) return null;
    const chosen = path.resolve(filePath);
    const open = path.resolve(openProject);
    return chosen === open || sameFile(chosen, open) ? "open" : null;
  }

  /**
   * A suggested path that is never the working slot: a project NAMED
   * "Default" (or "default.project", v3's slot) is offered
   * `Default 2.chiphippo` beside it rather than the app's own file.
   */
  besideSlot(filePath) {
    if (!this.isSlotPath(filePath)) return filePath;
    const stem = nameFromFile(filePath);
    return path.join(path.dirname(filePath), `${stem} 2${PROJECT_EXT}`);
  }

  /**
   * Move the working slot ASIDE, under a name of its own beside it, rather
   * than delete it: a recovery that could not be READ (an I/O failure, not a
   * damaged file) may hold the only copy of the work, and the slot is about to
   * become the next project's home, whose first autosave would write over it.
   * Where it went, or null when there was nothing to move. Throws when the
   * move itself fails — the caller must then leave the slot alone. The name
   * keeps the project extension, so File ▸ Open can reach it once whatever
   * kept it from being read is fixed.
   */
  setAsideDefaultProject() {
    const from = this.defaultProjectPath;
    if (!existsSafe(from)) return null;
    const stem = from.slice(0, -PROJECT_EXT.length);
    const to = `${stem}.unread-${Date.now()}${PROJECT_EXT}`;
    fs.renameSync(from, to);
    return to;
  }

  /**
   * FIRST LAUNCH AFTER v4: the working slot may still be a v3
   * `default.project.chiphippo` pointing at app-kept desktop files. Inline it
   * into `default.chiphippo`, then — and only then — remove the v3 file and
   * the desktop files it alone pointed at. A desktop the user had saved
   * somewhere of their own is read and LEFT WHERE IT IS.
   *
   * The warnings come back rather than being written: they belong to this ONE
   * migration, and the upgraded file has nothing to say about a desktop whose
   * v3 path had already gone. Startup hands them to the renderer, which is the
   * only chance the user gets to be told.
   *
   * @returns {Array<string>|null} the migration's warnings (possibly empty),
   *   or null when there was nothing to upgrade.
   */
  upgradeLegacyDefault() {
    const legacy = this.legacyDefaultProjectPath;
    if (!existsSafe(legacy)) return null;
    // A v4 working slot already exists (an older build ran again, a backup
    // was restored): it is the NEWER work, so it is never overwritten. The
    // v3 file — and the desktops it points at — are set aside, not deleted.
    if (existsSafe(this.defaultProjectPath)) {
      const kept = `${legacy}.v3-backup`;
      try {
        fs.renameSync(legacy, kept);
      } catch {
        return null; // leave it; the slot is untouched either way
      }
      return [
        `An older working project was found beside the current one and kept as ${path.basename(kept)}.`, // prettier-ignore
      ];
    }
    const raw = io.readJSON(legacy);
    const inlined = new Set(); // the desktop files actually read into it
    const meta = this.read(legacy, { inlined });
    if (!meta) {
      unlinkSafe(legacy); // not a project at all; the slot is simply empty
      return null;
    }
    this.write(this.defaultProjectPath, meta);
    unlinkSafe(legacy);
    for (const tab of raw?.tabs ?? []) {
      const stored = typeof tab?.file === "string" ? tab.file : "";
      if (!stored) continue;
      const file =
        tab.defaultFile === true
          ? path.join(this._saves, path.basename(stored))
          : path.resolve(stored);
      // The location decides, exactly as it always did: inside the app's own
      // folder it was the app's to keep, and anywhere else it is the user's.
      // And only one whose document is now IN the project: a file that could
      // not be read (permissions, a disk error) is all that is left of it.
      if (this.isInsideSaves(file) && inlined.has(path.resolve(file))) {
        unlinkSafe(file);
      }
    }
    return meta.warnings ?? [];
  }
}

/** A one-desktop project around a snapshot (a loose design opened as one). */
function projectOf(snapshot) {
  return {
    version: PROJECT_VERSION,
    name: snapshot.name,
    description: "",
    activeTab: "t1",
    nextIndex: 2,
    tabs: [
      {
        id: "t1",
        name: snapshot.name || "Desktop 1",
        ...(snapshot.description ? { description: snapshot.description } : {}),
        doc: snapshot.doc,
      },
    ],
    customChips: snapshot.customChips ?? [],
  };
}

/** fs.existsSync that can never throw a load. */
function existsSafe(filePath) {
  try {
    return fs.existsSync(filePath);
  } catch {
    return false;
  }
}

/**
 * Do two paths name ONE file — by identity, not spelling? A case-insensitive
 * volume (the default on macOS and Windows) opens `Default.chiphippo` as
 * `default.chiphippo`, and a symlinked folder is another spelling still. False
 * when either is missing (or unreadable): only a file can be the same file.
 */
function sameFile(a, b) {
  try {
    const x = fs.statSync(a);
    const y = fs.statSync(b);
    // A zero inode is "unknown" on some volumes, not a shared one.
    return x.ino !== 0 && x.dev === y.dev && x.ino === y.ino;
  } catch {
    return false;
  }
}

/** Delete a file; missing is not an error. Returns whether one went. */
function unlinkSafe(filePath) {
  try {
    fs.unlinkSync(filePath);
    return true;
  } catch (err) {
    if (err.code === "ENOENT") return false;
    console.error("[store] project cleanup:", err && err.message);
    return false;
  }
}

module.exports = {
  ProjectStore,
  suggestFileName,
  nameFromFile,
  PROJECT_VERSION,
  PROJECT_EXT,
  DESKTOP_EXT,
  LEGACY_PROJECT_EXT,
  DEFAULT_PROJECT_FILE,
  SAVES_DIR,
};
